/**
 * Utilidades para detectar dispositivos basándose en dimensiones de pantalla
 */

export interface ScreenSizeInfo {
  width: number;
  height: number;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isSmallMobile: boolean;
  isLargeMobile: boolean;
  isSmallTablet: boolean;
  isLargeTablet: boolean;
  orientation: 'portrait' | 'landscape';
  aspectRatio: number;
  deviceCategory: 'mobile' | 'tablet' | 'desktop';
}

/**
 * Obtiene las dimensiones actuales de la pantalla
 */
export function getScreenDimensions(): { width: number; height: number } {
  return {
    width: window.innerWidth,
    height: window.innerHeight
  };
}

/**
 * Detecta el tipo de dispositivo basándose únicamente en dimensiones
 */
export function detectDeviceBySize(): ScreenSizeInfo {
  const { width, height } = getScreenDimensions();
  const aspectRatio = width / height;
  const orientation = width > height ? 'landscape' : 'portrait';

  // Definición de breakpoints
  const breakpoints = {
    mobile: 768,
    tablet: 1024,
    smallMobile: 480,
    largeMobile: 600,
    smallTablet: 900
  };

  // Detección basada en ancho
  const isSmallMobile = width <= breakpoints.smallMobile;
  const isLargeMobile = width > breakpoints.smallMobile && width <= breakpoints.largeMobile;
  const isMobile = width <= breakpoints.mobile;
  const isSmallTablet = width > breakpoints.mobile && width <= breakpoints.smallTablet;
  const isLargeTablet = width > breakpoints.smallTablet && width <= breakpoints.tablet;
  const isTablet = width > breakpoints.mobile && width <= breakpoints.tablet;
  const isDesktop = width > breakpoints.tablet;

  // Categoría del dispositivo
  let deviceCategory: 'mobile' | 'tablet' | 'desktop' = 'desktop';
  if (isMobile) deviceCategory = 'mobile';
  else if (isTablet) deviceCategory = 'tablet';

  return {
    width,
    height,
    isMobile,
    isTablet,
    isDesktop,
    isSmallMobile,
    isLargeMobile,
    isSmallTablet,
    isLargeTablet,
    orientation,
    aspectRatio,
    deviceCategory
  };
}

/**
 * Detecta si es móvil basándose en dimensiones
 */
export function isMobileBySize(): boolean {
  return window.innerWidth <= 768;
}

/**
 * Detecta si es tablet basándose en dimensiones
 */
export function isTabletBySize(): boolean {
  const width = window.innerWidth;
  return width > 768 && width <= 1024;
}

/**
 * Detecta si es escritorio basándose en dimensiones
 */
export function isDesktopBySize(): boolean {
  return window.innerWidth > 1024;
}

/**
 * Detecta si es un dispositivo pequeño (móvil pequeño)
 */
export function isSmallMobileBySize(): boolean {
  return window.innerWidth <= 480;
}

/**
 * Detecta si es un móvil grande o tablet pequeña
 */
export function isLargeMobileBySize(): boolean {
  const width = window.innerWidth;
  return width > 480 && width <= 768;
}

/**
 * Obtiene la categoría del dispositivo basándose en dimensiones
 */
export function getDeviceCategoryBySize(): 'mobile' | 'tablet' | 'desktop' {
  const width = window.innerWidth;
  
  if (width <= 768) return 'mobile';
  if (width <= 1024) return 'tablet';
  return 'desktop';
}

/**
 * Detecta la orientación basándose en dimensiones
 */
export function getOrientationBySize(): 'portrait' | 'landscape' {
  return window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
}

/**
 * Verifica si las dimensiones coinciden con un rango específico
 */
export function isInSizeRange(minWidth: number, maxWidth: number): boolean {
  const width = window.innerWidth;
  return width >= minWidth && width <= maxWidth;
}

/**
 * Detecta si es un dispositivo con pantalla cuadrada o casi cuadrada
 */
export function isSquareScreen(): boolean {
  const { width, height } = getScreenDimensions();
  const aspectRatio = width / height;
  return aspectRatio >= 0.9 && aspectRatio <= 1.1;
}

/**
 * Detecta si es un dispositivo con pantalla ultra ancha
 */
export function isUltraWideScreen(): boolean {
  const { width, height } = getScreenDimensions();
  const aspectRatio = width / height;
  return aspectRatio > 2.0;
}

/**
 * Obtiene información detallada de la pantalla
 */
export function getDetailedScreenInfo() {
  const { width, height } = getScreenDimensions();
  const aspectRatio = width / height;
  const orientation = width > height ? 'landscape' : 'portrait';
  
  return {
    dimensions: { width, height },
    aspectRatio: Math.round(aspectRatio * 100) / 100,
    orientation,
    isSquare: aspectRatio >= 0.9 && aspectRatio <= 1.1,
    isWide: aspectRatio > 1.5,
    isUltraWide: aspectRatio > 2.0,
    pixelDensity: window.devicePixelRatio || 1,
    availableWidth: window.screen.availWidth,
    availableHeight: window.screen.availHeight,
    colorDepth: window.screen.colorDepth,
    pixelDepth: window.screen.pixelDepth
  };
}
